import { Application } from "@/api";
import agents from "@/api/mock-data/agents/list";
import { mcpServers } from "@/api/mock-data/mcp/data";

/**
 * Per-entity mock store for the Applications API surface.
 *
 * One singleton owns the seed and exposes CRUD methods so the list mock and
 * the create/update/delete mocks share a single in-memory source of truth.
 * Session-scoped: state lives in module memory and resets on page refresh
 * (cleared explicitly on logout via resetAllMockStores).
 */
class ApplicationStore {
  private items: Application[] = ApplicationStore.cloneSeed();

  private static cloneSeed(): Application[] {
    // structuredClone handles Date instances correctly (seed contains real Date objects).
    return structuredClone([...agents, ...mcpServers]);
  }

  list(filter?: { type?: string }): Application[] {
    if (!filter?.type) return this.items;
    return this.items.filter(i => i.type === filter.type);
  }

  get(id: string): Application | undefined {
    return this.items.find(i => i.id === id);
  }

  create(payload: Partial<Application>): Application {
    const now = new Date();
    const id =
      payload.id ||
      `mock-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const next: Application = {
      ...payload,
      id,
      tlxMcpUrl: payload.tlxMcpUrl ?? `https://trustlogix.io/mcp/${id}`,
      createdDate: now,
      lastModifiedDate: now,
      deleted: false,
      status: payload.status || "ACTIVE",
    } as Application;
    this.items = [next, ...this.items];
    return next;
  }

  update(id: string, patch: Partial<Application>): Application | undefined {
    this.items = this.items.map(i =>
      i.id === id ? { ...i, ...patch, lastModifiedDate: new Date() } : i,
    );
    return this.get(id);
  }

  remove(id: string): { success: boolean } {
    this.items = this.items.filter(i => i.id !== id);
    return { success: true };
  }

  reset(): void {
    this.items = ApplicationStore.cloneSeed();
  }
}

export const applicationStore = new ApplicationStore();
